/**
 * Virtual Host Call Log Page
 *
 * Clean, minimal design matching Order Queue style.
 * No header - just title with date filter and close button.
 *
 * Features:
 * - Paginated call list with date filtering
 * - Click to view call details modal
 * - On-demand transcript fetching from Hume
 * - Audio download links
 *
 * Author: Dine AI
 * Created: December 16, 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone, Clock, User, Calendar, ChevronLeft, ChevronRight,
  FileText, Download, X, Loader2, CheckCircle, RefreshCw,
  XCircle, PhoneOff, PhoneMissed, ShoppingBag, Maximize2
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Fetch helper with credentials for VH Staff httpOnly cookie auth
 */
const fetchWithCredentials = async (url: string, options: RequestInit = {}) => {
  const response = await fetch(`/api${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    throw new Error(`API request failed: ${response.status}`);
  }
  return response.json();
};

interface CallSummary {
  call_id: number;
  hume_chat_id: string | null;
  customer_phone: string | null;
  customer_name: string | null;
  call_started_at: string | null;
  call_ended_at: string | null;
  call_duration_seconds: number | null;
  call_status: string | null;
  order_placed: boolean;
  order_id: number | null;
}

interface TranscriptEntry {
  role: 'user' | 'agent';
  text: string;
  timestamp: string | null;
}

interface CallListResponse {
  calls: CallSummary[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: '7 Days' },
  { value: 'last_30_days', label: '30 Days' }
];

/**
 * Format phone number for display
 */
const formatPhone = (phone: string | null): string => {
  if (!phone) return 'Unknown';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

/**
 * Format duration in MM:SS
 */
const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format date/time for display
 * Database stores naive UTC datetimes, so we append 'Z' to ensure
 * JavaScript interprets them as UTC before converting to local time
 */
const formatDateTime = (dateStr: string | null): string => {
  if (!dateStr) return 'Unknown';
  // Append 'Z' if not present to indicate UTC
  const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  const date = new Date(utcDateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format current date/time for header
 */
const formatHeaderDateTime = (): string => {
  const now = new Date();
  return now.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Get status icon and color
 */
const getStatusInfo = (status: string | null) => {
  switch (status) {
    case 'completed':
      return { icon: CheckCircle, color: 'text-green-600', label: 'Completed' };
    case 'transferred':
      return { icon: Phone, color: 'text-blue-600', label: 'Transferred' };
    case 'abandoned':
      return { icon: PhoneMissed, color: 'text-yellow-600', label: 'Abandoned' };
    case 'failed':
      return { icon: XCircle, color: 'text-red-600', label: 'Failed' };
    default:
      return { icon: Phone, color: 'text-gray-500', label: status || 'Unknown' };
  }
};

const VirtualHostCallLog: React.FC = () => {
  usePageTitle('vhCallLog');
  const navigate = useNavigate();
  const [calls, setCalls] = useState<CallSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState('last_30_days');

  // Modal state
  const [selectedCall, setSelectedCall] = useState<CallSummary | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [audioStatusText, setAudioStatusText] = useState('');

  // Hide chat widget on this page to avoid overlapping with pagination
  useEffect(() => {
    const chatWidget = document.getElementById('chat-widget-container');
    if (chatWidget) {
      chatWidget.style.display = 'none';
    }
    return () => {
      if (chatWidget) {
        chatWidget.style.display = '';
      }
    };
  }, []);

  /**
   * Fetch calls list
   */
  const fetchCalls = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const data = await fetchWithCredentials(`/virtual-host/call-log?page=${page}&limit=15&date_range=${dateRange}`);

      if (data) {
        setCalls(data.calls || []);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
      }
    } catch (error: any) {
      console.error('Error fetching calls:', error);
      toast.error('Failed to load call history');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCalls();
  }, [page, dateRange]);

  /**
   * Fetch transcript for selected call
   */
  const fetchTranscript = async (callId: number) => {
    setLoadingTranscript(true);
    setTranscript([]);
    try {
      const data = await fetchWithCredentials(`/virtual-host/call-log/${callId}/transcript`);
      if (data?.transcript) {
        setTranscript(data.transcript);
      }
    } catch (error: any) {
      console.error('Error fetching transcript:', error);
      toast.error('Failed to load transcript');
    } finally {
      setLoadingTranscript(false);
    }
  };

  /**
   * Download audio for selected call.
   * Hume builds audio on-demand — first request triggers reconstruction,
   * so we auto-poll until COMPLETE (up to ~60 seconds).
   */
  const downloadAudio = async (callId: number) => {
    setLoadingAudio(true);
    setAudioStatusText('Requesting audio...');

    const MAX_ATTEMPTS = 15;
    const POLL_INTERVAL_MS = 4000;

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        const data = await fetchWithCredentials(`/virtual-host/call-log/${callId}/audio`);

        if (data.status === 'COMPLETE' && data.audio_url) {
          setAudioStatusText('');
          window.open(data.audio_url, '_blank');
          toast.success('Audio download started');
          return;
        }

        if (data.status === 'ERROR' || data.status === 'CANCELLED') {
          setAudioStatusText('');
          toast.error(data.message || 'Audio not available');
          return;
        }

        // QUEUED or IN_PROGRESS — show progress and retry
        if (attempt < MAX_ATTEMPTS) {
          const statusLabel = data.status === 'QUEUED' ? 'Preparing audio' : 'Processing audio';
          setAudioStatusText(`${statusLabel}... (${attempt}/${MAX_ATTEMPTS})`);
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        } else {
          setAudioStatusText('');
          toast.info('Audio is still processing. Please try again in a minute.');
        }
      }
    } catch (error: any) {
      console.error('Error fetching audio:', error);
      setAudioStatusText('');
      toast.error('Failed to get audio download');
    } finally {
      setLoadingAudio(false);
      setAudioStatusText('');
    }
  };

  /**
   * Open call detail modal
   */
  const openCallDetail = (call: CallSummary) => {
    setSelectedCall(call);
    setTranscript([]);
  };

  /**
   * Close modal
   */
  const closeModal = () => {
    setSelectedCall(null);
    setTranscript([]);
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-50 overflow-hidden">
      <div className="h-full flex flex-col p-6">
        {/* Header - Clean style like Order Queue */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-baseline gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">Call Log</h1>
            <span className="text-sm text-gray-400">{formatHeaderDateTime()}</span>
          </div>

          <div className="flex items-center gap-3">
            {/* Date Filter - Modern pill buttons */}
            <div className="flex items-center gap-1 p-1 bg-gray-100 border border-gray-200 rounded-xl">
              {DATE_RANGES.map(range => (
                <button
                  key={range.value}
                  onClick={() => { setDateRange(range.value); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    dateRange === range.value
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>

            {/* Total Count */}
            <span className="text-gray-500 text-sm">{total} calls</span>

            {/* Refresh */}
            <button
              onClick={() => fetchCalls(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-lg text-gray-700 text-sm transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {/* Close - go back to previous page */}
            <button
              onClick={() => navigate(-1)}
              className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Call List */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-gray-200 text-gray-500 text-sm font-medium bg-gray-50">
            <div className="col-span-3">Date & Time</div>
            <div className="col-span-3">Customer</div>
            <div className="col-span-2">Duration</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Order</div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
              </div>
            ) : calls.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <PhoneOff className="w-12 h-12 mb-4" />
                <p>No calls found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {calls.map((call) => {
                  const statusInfo = getStatusInfo(call.call_status);
                  const StatusIcon = statusInfo.icon;

                  return (
                    <div
                      key={call.call_id}
                      onClick={() => openCallDetail(call)}
                      className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      {/* Date/Time */}
                      <div className="col-span-3 flex items-center">
                        <span className="text-gray-900">{formatDateTime(call.call_started_at)}</span>
                      </div>

                      {/* Customer */}
                      <div className="col-span-3">
                        <p className="text-gray-900">{call.customer_name || 'Unknown'}</p>
                        <p className="text-gray-400 text-sm">{formatPhone(call.customer_phone)}</p>
                      </div>

                      {/* Duration */}
                      <div className="col-span-2 flex items-center text-gray-900">
                        {formatDuration(call.call_duration_seconds)}
                      </div>

                      {/* Status */}
                      <div className="col-span-2 flex items-center">
                        <div className={`flex items-center gap-2 ${statusInfo.color}`}>
                          <StatusIcon className="w-4 h-4" />
                          <span className="text-sm">{statusInfo.label}</span>
                        </div>
                      </div>

                      {/* Order */}
                      <div className="col-span-2 flex items-center">
                        {call.order_placed ? (
                          <div className="flex items-center gap-2 text-green-600">
                            <ShoppingBag className="w-4 h-4" />
                            <span>#{call.order_id}</span>
                          </div>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-gray-500 text-sm">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg bg-white hover:bg-gray-100 border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg bg-white hover:bg-gray-100 border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Call Detail Modal */}
      <AnimatePresence>
        {selectedCall && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl max-h-[80vh] bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Call Details</h2>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Call Info */}
              <div className="px-6 py-4 border-b border-gray-200 space-y-3">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium text-lg">{selectedCall.customer_name || 'Unknown'}</p>
                    <p className="text-gray-500">{formatPhone(selectedCall.customer_phone)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDateTime(selectedCall.call_started_at)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>Duration: {formatDuration(selectedCall.call_duration_seconds)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const si = getStatusInfo(selectedCall.call_status);
                      const Icon = si.icon;
                      return (
                        <div className={`flex items-center gap-2 ${si.color}`}>
                          <Icon className="w-4 h-4" />
                          <span>{si.label}</span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedCall.order_placed ? (
                      <div className="flex items-center gap-2 text-green-600">
                        <ShoppingBag className="w-4 h-4" />
                        <span>Order #{selectedCall.order_id}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">No order placed</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="px-6 py-4 border-b border-gray-200 flex gap-3">
                <button
                  onClick={() => selectedCall && fetchTranscript(selectedCall.call_id)}
                  disabled={loadingTranscript || !selectedCall.hume_chat_id}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-200 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
                >
                  {loadingTranscript ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <FileText className="w-5 h-5" />
                  )}
                  <span>{loadingTranscript ? 'Loading...' : 'View Transcript'}</span>
                </button>

                <button
                  onClick={() => selectedCall && downloadAudio(selectedCall.call_id)}
                  disabled={loadingAudio || !selectedCall.hume_chat_id}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed text-gray-700 rounded-xl font-medium transition-colors border border-gray-200"
                >
                  {loadingAudio ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                  <span>{loadingAudio ? (audioStatusText || 'Loading...') : 'Download Audio'}</span>
                </button>
              </div>

              {/* Transcript Area */}
              <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50">
                {!selectedCall.hume_chat_id ? (
                  <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                    <FileText className="w-10 h-10 mb-3" />
                    <p>Transcript not available for this call</p>
                  </div>
                ) : transcript.length === 0 && !loadingTranscript ? (
                  <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                    <FileText className="w-10 h-10 mb-3" />
                    <p>Click "View Transcript" to load the conversation</p>
                  </div>
                ) : loadingTranscript ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {transcript.map((entry, index) => {
                      const tsMs = entry.timestamp ? parseInt(entry.timestamp, 10) : null;
                      const prevTs = index > 0 && transcript[index - 1].timestamp
                        ? parseInt(transcript[index - 1].timestamp!, 10)
                        : null;
                      const deltaMs = tsMs && prevTs ? tsMs - prevTs : null;
                      const timeStr = tsMs
                        ? new Date(tsMs).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
                          + '.' + String(tsMs % 1000).padStart(3, '0')
                        : null;

                      return (
                        <div key={index}>
                          {/* Delta badge between messages */}
                          {deltaMs !== null && deltaMs > 0 && (
                            <div className="flex justify-center mb-1">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                deltaMs > 5000 ? 'bg-red-100 text-red-600' :
                                deltaMs > 2000 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-400'
                              }`}>
                                +{deltaMs >= 1000 ? (deltaMs / 1000).toFixed(1) + 's' : deltaMs + 'ms'}
                              </span>
                            </div>
                          )}
                          <div className={`flex gap-3 ${entry.role === 'user' ? 'flex-row-reverse' : ''}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              entry.role === 'user' ? 'bg-blue-100' : 'bg-purple-100'
                            }`}>
                              {entry.role === 'user' ? (
                                <User className="w-4 h-4 text-blue-600" />
                              ) : (
                                <Phone className="w-4 h-4 text-purple-600" />
                              )}
                            </div>
                            <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                              entry.role === 'user'
                                ? 'bg-blue-100 text-blue-900'
                                : 'bg-white border border-gray-200 text-gray-700'
                            }`}>
                              <p className="text-sm">{entry.text}</p>
                              {timeStr && (
                                <p className="text-[10px] text-gray-400 mt-1">{timeStr}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VirtualHostCallLog;
